/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { AdvancePaymentResponse } from '../models/advance-payment-response';
import { BusinessType } from '../models/business-type';
import { CreateAdvancePaymentRequest } from '../models/create-advance-payment-request';
import { GenericResponse } from '../models/generic-response';
import { GetAdvancePaymentResponse } from '../models/get-advance-payment-response';
import { PaymentFileResponse } from '../models/payment-file-response';
import { UpdateAdvancePaymentRequest } from '../models/update-advance-payment-request';

/**
 * Advance payment tracking APIs
 */
@Injectable({ providedIn: 'root' })
export class AdvancePaymentsService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllPayments()` */
  static readonly GetAllPaymentsPath = '/api/v1/advance_payment';

  /**
   * Get list of payments.
   *
   * Gets the list of all the payments with any of the filters applied. <br>
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllPayments()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllPayments$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to following fields:
       * - client name
       * - bank name predicate
       * - amount
       * - balance
       * - remittanceNumber
       * - date
       */
      search?: string;

      /**
       * this adds a string search to the following fields:
       *   - client
       */
      clientId?: number;

      /**
       * This allows filtering payments by their business type
       */
      businessType?: Array<BusinessType>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetAdvancePaymentResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AdvancePaymentsService.GetAllPaymentsPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
      rb.query('clientId', params.clientId, {});
      rb.query('businessType', params.businessType, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetAdvancePaymentResponse>;
        })
      );
  }

  /**
   * Get list of payments.
   *
   * Gets the list of all the payments with any of the filters applied. <br>
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllPayments$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllPayments(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to following fields:
       * - client name
       * - bank name predicate
       * - amount
       * - balance
       * - remittanceNumber
       * - date
       */
      search?: string;

      /**
       * this adds a string search to the following fields:
       *   - client
       */
      clientId?: number;

      /**
       * This allows filtering payments by their business type
       */
      businessType?: Array<BusinessType>;
    },
    context?: HttpContext
  ): Observable<GetAdvancePaymentResponse> {
    return this.getAllPayments$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<GetAdvancePaymentResponse>
        ): GetAdvancePaymentResponse => r.body
      )
    );
  }

  /** Path part for operation `createAdvancePayment()` */
  static readonly CreateAdvancePaymentPath = '/api/v1/advance_payment';

  /**
   * Create new advance payment.
   *
   * This API creates an advance payment ahead of the invoice payments
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createAdvancePayment()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createAdvancePayment$Response(
    params: {
      body: CreateAdvancePaymentRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<AdvancePaymentResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AdvancePaymentsService.CreateAdvancePaymentPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<AdvancePaymentResponse>;
        })
      );
  }

  /**
   * Create new advance payment.
   *
   * This API creates an advance payment ahead of the invoice payments
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createAdvancePayment$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createAdvancePayment(
    params: {
      body: CreateAdvancePaymentRequest;
    },
    context?: HttpContext
  ): Observable<AdvancePaymentResponse> {
    return this.createAdvancePayment$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<AdvancePaymentResponse>
        ): AdvancePaymentResponse => r.body
      )
    );
  }

  /** Path part for operation `getPaymentById()` */
  static readonly GetPaymentByIdPath = '/api/v1/advance_payment/{id}';

  /**
   * fetch payment details.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPaymentById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPaymentById$Response(
    params: {
      /**
       * The payments id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<AdvancePaymentResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AdvancePaymentsService.GetPaymentByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<AdvancePaymentResponse>;
        })
      );
  }

  /**
   * fetch payment details.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getPaymentById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPaymentById(
    params: {
      /**
       * The payments id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<AdvancePaymentResponse> {
    return this.getPaymentById$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<AdvancePaymentResponse>
        ): AdvancePaymentResponse => r.body
      )
    );
  }

  /** Path part for operation `deleteAdvancePaymentById()` */
  static readonly DeleteAdvancePaymentByIdPath = '/api/v1/advance_payment/{id}';

  /**
   * Delete advance payment by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteAdvancePaymentById()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteAdvancePaymentById$Response(
    params: {
      /**
       * The advance payment id that needs to be delete
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AdvancePaymentsService.DeleteAdvancePaymentByIdPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Delete advance payment by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteAdvancePaymentById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteAdvancePaymentById(
    params: {
      /**
       * The advance payment id that needs to be delete
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.deleteAdvancePaymentById$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updatePaymentById()` */
  static readonly UpdatePaymentByIdPath = '/api/v1/advance_payment/{id}';

  /**
   * Update payment by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updatePaymentById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updatePaymentById$Response(
    params: {
      /**
       * The payment id that needs to be updated
       */
      id: number;
      body: UpdateAdvancePaymentRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<AdvancePaymentResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AdvancePaymentsService.UpdatePaymentByIdPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<AdvancePaymentResponse>;
        })
      );
  }

  /**
   * Update payment by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updatePaymentById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updatePaymentById(
    params: {
      /**
       * The payment id that needs to be updated
       */
      id: number;
      body: UpdateAdvancePaymentRequest;
    },
    context?: HttpContext
  ): Observable<AdvancePaymentResponse> {
    return this.updatePaymentById$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<AdvancePaymentResponse>
        ): AdvancePaymentResponse => r.body
      )
    );
  }

  /** Path part for operation `uploadPaymentFile()` */
  static readonly UploadPaymentFilePath =
    '/api/v1/advance_payments/upload-file';

  /**
   * upload file.
   *
   * This API used to upload files.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `uploadPaymentFile()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadPaymentFile$Response(
    params: {
      body: {
        /**
         * An array of attachment files
         */
        files?: Array<Blob>;
      };
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PaymentFileResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AdvancePaymentsService.UploadPaymentFilePath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PaymentFileResponse>;
        })
      );
  }

  /**
   * upload file.
   *
   * This API used to upload files.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `uploadPaymentFile$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadPaymentFile(
    params: {
      body: {
        /**
         * An array of attachment files
         */
        files?: Array<Blob>;
      };
    },
    context?: HttpContext
  ): Observable<PaymentFileResponse> {
    return this.uploadPaymentFile$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PaymentFileResponse>): PaymentFileResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `downloadFileById()` */
  static readonly DownloadFileByIdPath =
    '/api/v1/advance_payments/download-file/{id}';

  /**
   * To download advance payment file by id.
   *
   * download the file using  id. <br>
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `downloadFileById()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadFileById$Response(
    params: {
      /**
       * The file id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Blob>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AdvancePaymentsService.DownloadFileByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'blob', accept: 'application/*', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Blob>;
        })
      );
  }

  /**
   * To download advance payment file by id.
   *
   * download the file using  id. <br>
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `downloadFileById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadFileById(
    params: {
      /**
       * The file id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<Blob> {
    return this.downloadFileById$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }
}
